import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import {
  DEPARTURE_REJECTED_STATUS,
  DEPARTURE_STATUS,
} from "@features/vessel-call/vessel-call-status/Departure/constants";
import type { Company } from "@services/vessel-call/types";
import type { TFunction } from "i18next";

export const LANG_BASE = "vessel-call";
export const REJECTED_STATUS_FILTER_VALUE = "REJECTED";
const DEPARTURE_STATUS_LABEL_BASE = "vessel-call.departure-status-page.status-labels";

const STATUS_FILTER_GROUPS: Record<string, string[]> = {
  [REJECTED_STATUS_FILTER_VALUE]: ["REJECTED_MARINE_SUPERVISOR", "REJECTED_MARINE_OPERATOR"],
};

const expandFilterValues = (field: string, value: string): string[] => {
  if (field !== "status") return [value];
  return STATUS_FILTER_GROUPS[value] ?? [value];
};

export function findPortLabel(
  portsOptions: { value?: string; label: string }[],
  code?: string,
  na?: string,
) {
  return portsOptions.find((p) => p.value === code)?.label ?? na;
}

export function findCompanyName(companyOptions: Company[], agencyName: string, na?: string) {
  return companyOptions.find((c) => c.companyCode === agencyName)?.companyNameEnglish ?? na;
}

export function buildFilterPayload(
  page: number,
  pageSize: number,
  filters: FilterItem[],
): { pageSize: number; pageNumber: number; filters: { field: string; values: string[] }[] } {
  const payload = {
    pageSize,
    pageNumber: page,
    filters: [] as { field: string; values: string[] }[],
  };

  filters.forEach(({ field, value }) => {
    const normalized =
      typeof value === "string"
        ? value.trim()
        : ((value as any)?.from ?? (value as any)?.to ?? String(value ?? ""));
    if (!normalized) return;
    const values = expandFilterValues(field, normalized);
    const existing = payload.filters.find((f) => f.field === field);
    if (existing) {
      for (const currentValue of values) {
        if (!existing.values.includes(currentValue)) {
          existing.values.push(currentValue);
        }
      }
    } else {
      payload.filters.push({ field, values });
    }
  });

  return payload;
}

export function formatRestriction(
  restriction: string | null | undefined,
  t: TFunction,
  notApplicable: string,
): string {
  if (!restriction) return notApplicable;
  return t(`${LANG_BASE}.restrictions.${restriction}`);
}

export const PORT_CALLS_FIELDS = Object.freeze({
  IMO: "imo",
  VCN: "vcn",
  SUBMITTED_AT: "submittedAt",
  TYPE: "type",
  NAME: "name",
  FLAG: "flag",
  AGENCY_NAME: "agencyName",
  LAST_PORT: "lastPort",
  NEXT_PORT: "nextPort",
  VISIT_PURPOSE: "visitPurpose",
  ETA: "eta",
  ARRIVAL_LOCATION: "arrivalLocation",
  ATA: "ata",
  ATD: "atd",
  CURRENT_LOCATION: "currentLocation",
  VERIFIED_BY: "verifiedBy",
  APPROVED_BY: "approvedBy",
  DEPARTURE_STATUS: "departureStatus",
  STATUS: "status",
});

interface StatusMappingEntry {
  label: string;
}

interface StatusFilterEntry {
  value: string;
  label: string;
}

export type DepartureStatusBadgeType = "success" | "warning" | "error";

export function getDepartureStatusBadgeType(status?: string | null): DepartureStatusBadgeType {
  if (status === DEPARTURE_STATUS.APPROVED_BY_MARINE_DEPARTMENT) return "success";
  if (
    status &&
    (DEPARTURE_REJECTED_STATUS.includes(status) || status === DEPARTURE_STATUS.CANCELLED)
  )
    return "error";
  return "warning";
}

const STATUS_MAP: Record<string, StatusMappingEntry> = {
  PENDING_PRE_APPROVAL: { label: "pending-verification" },
  PRE_APPROVED: { label: "verified" },
  PENDING_APPROVAL: { label: "pending-approval" },
  APPROVED: { label: "approved" },
  REJECTED_MARINE_SUPERVISOR: { label: "rejected" },
  REJECTED_MARINE_OPERATOR: { label: "rejected" },
  INTERRUPTED_IN_PORT: { label: "interrupted-in-port" },
  INTERRUPTED_LEAVE_PORT: { label: "interrupted-leave-port" },
  PRE_ARRIVAL_DRAFT: { label: "pre-arrival-draft" },
  ARRIVAL_DRAFT: { label: "arrival-draft" },
  EXPIRED: { label: "expired" },
  RESUME_PENDING: { label: "resume-pending" },
  DEPARTURE_REQUESTED: { label: "departure-requested" },
  DEPARTURE_AWAITING_MARINE_APPROVAL: {
    label: "departure-awaiting-marine-approval",
  },
  DEPARTURE_AWAITING_TERMINAL_APPROVAL: {
    label: "departure-awaiting-terminal-approval",
  },
  DEPARTURE_APPROVED: { label: "departure-approved" },
  DEPARTED: { label: "departed" },
  CLOSED: { label: "closed" },
  REINSTATED: { label: "reinstated" },
};

export function getStatusInfo(rawStatus?: string): string {
  if (!rawStatus) return "common.unknown";
  const entry = STATUS_MAP[rawStatus];
  if (!entry) return "common.unknown";
  return `${LANG_BASE}.status.${entry.label}`;
}

const groupedStatusValues = new Set(Object.values(STATUS_FILTER_GROUPS).flat());

export const statuses: StatusFilterEntry[] = [
  ...Object.entries(STATUS_MAP)
    .filter(([value]) => !groupedStatusValues.has(value))
    .map(([value, { label }]) => ({ value, label })),
  { value: REJECTED_STATUS_FILTER_VALUE, label: "rejected" },
];

export const departureStatuses: StatusFilterEntry[] = Object.values(DEPARTURE_STATUS).map(
  (status) => ({
    value: status,
    label: `${DEPARTURE_STATUS_LABEL_BASE}.${status}`,
  }),
);

const departureStatusInfoByValue = new Map(
  departureStatuses.map((statusInfo) => [statusInfo.value, statusInfo]),
);

export function getDepartureStatusInfo(
  rawStatus?: string | null,
): StatusFilterEntry & { badgeType?: DepartureStatusBadgeType } {
  if (!rawStatus) {
    return { value: "", label: "-" };
  }

  const baseInfo = departureStatusInfoByValue.get(rawStatus) ?? {
    value: rawStatus,
    label: "common.unknown",
  };

  return {
    ...baseInfo,
    badgeType: getDepartureStatusBadgeType(rawStatus),
  };
}

export function formatDateTime(
  dateString: string | null,
  notApplicable?: string,
): string | undefined {
  if (!dateString) return notApplicable;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}\n${hh}:${min}`;
}
export function sortAlphabetically<T extends { label: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.label.localeCompare(b.label));
}
